import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { signup, logout } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../Components/shared/SEO';

const ROLE_DASHBOARD = {
  client: '/',
  therapist: '/therapist',
  venue: '/venue'
};

const SignupPage = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    role: 'client',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: '',
    businessType: 'room_partner',
    agreedToCommission: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const { confirmPassword, ...signupData } = form;
      await signup(signupData);

      if (form.role === 'client') {
        // Clients sign in explicitly afterward rather than being auto-logged-in,
        // so "create account" and "log in" stay two distinct, understandable steps.
        await logout();
        navigate('/login');
      } else {
        await refresh();
        navigate(ROLE_DASHBOARD[form.role]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-12">
      <SEO
        title="Sign Up"
        path="/signup"
        description="Create a Serenoly account as a client, massage therapist, or venue partner — join Nigeria's verified massage therapist marketplace."
      />
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Create an account</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">I am a</label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="client">Client looking for a massage</option>
              <option value="therapist">Massage therapist</option>
              <option value="venue">Hotel / venue partner</option>
            </select>
          </div>

          <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name"
            className="w-full p-3 border rounded-lg" required />
          <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name"
            className="w-full p-3 border rounded-lg" required />
          <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email"
            className="w-full p-3 border rounded-lg" required />
          <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone number"
            className="w-full p-3 border rounded-lg" required />
          <div className="relative">
            <input name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange}
              placeholder="Password" className="w-full p-3 border rounded-lg pr-12" required minLength={6} />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="relative">
            <input name="confirmPassword" type={showPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={handleChange}
              placeholder="Confirm password" className="w-full p-3 border rounded-lg pr-12" required minLength={6} />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {form.role === 'venue' && (
            <>
              <input name="businessName" value={form.businessName} onChange={handleChange}
                placeholder="Business name" className="w-full p-3 border rounded-lg" required />
              <select name="businessType" value={form.businessType} onChange={handleChange}
                className="w-full p-3 border rounded-lg">
                <option value="room_partner">Room partner (host massage sessions)</option>
                <option value="full_business">Full business (offer our own spa services)</option>
                <option value="both">Both</option>
              </select>
              <label className="flex items-start gap-2 text-sm bg-accent-lighter p-3 rounded-lg">
                <input type="checkbox" name="agreedToCommission" checked={form.agreedToCommission} onChange={handleChange}
                  className="mt-0.5 shrink-0" required />
                <span>I agree that Serenoly takes a <strong>30% commission</strong> on room/venue booking fees earned through the platform.</span>
              </label>
            </>
          )}

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <button type="submit" disabled={isLoading}
            className={`w-full bg-black text-white p-3 rounded-lg font-medium hover:bg-gray-800 transition-colors ${isLoading ? 'opacity-50' : ''}`}>
            {isLoading ? 'Creating account...' : 'Sign up'}
          </button>
        </form>
        <p className="text-sm text-center text-gray-600 mt-4">
          Already have an account? <a href="/login" className="text-blue-600">Log in</a>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
